// Pricing calculator utility for taxi services
// Dynamic fare calculation based on distance and vehicle type

// 2026 Vehicle rates per kilometer (updated market rates)
export const VEHICLE_RATES_2026 = {
  marutiDzire: 11,
  toyotaEtios: 12,
  brezza: 14,
  marutiErtiga: 16,
  kiaCarens: 18,
  innovaCrysta: 21,
  innovaCrysta7Plus1: 22,
  innovaHycross: 26,
  tempoTraveller12: 22,
  tempoTraveller17: 28,
  tempoTraveller25: 35,
  urbania12: 30,
  urbania16: 35,
  forceUrbania: 38
};

// Vehicle display names
export const VEHICLE_NAMES = {
  marutiDzire: 'Maruti Dzire',
  toyotaEtios: 'Toyota Etios',
  brezza: 'Brezza',
  marutiErtiga: 'Maruti Ertiga',
  kiaCarens: 'Kia Carens',
  innovaCrysta: 'Innova Crysta',
  innovaCrysta7Plus1: 'Innova Crysta 7+1',
  innovaHycross: 'Innova Hycross',
  tempoTraveller12: 'Tempo Traveller 12 Seater',
  tempoTraveller17: 'Tempo Traveller 17 Seater',
  tempoTraveller25: 'Tempo Traveller 25 Seater',
  urbania12: 'Force Urbania 12 Seater',
  urbania16: 'Force Urbania 16 Seater',
  forceUrbania: 'Force Urbania'
};

/**
 * Calculate one-way fare based on distance, vehicle rate, and toll
 * @param {number} distanceKm - Distance in kilometers
 * @param {number} vehicleRate - Rate per kilometer for the vehicle
 * @param {number} toll - Toll charges (default 0)
 * @returns {number} - Total fare
 */
export const calculateOneWayFare = (distanceKm, vehicleRate, toll = 0) => {
  return Math.round((distanceKm * vehicleRate) + toll);
};

/**
 * Calculate fares for all vehicle types for a specific route
 * @param {number} distanceKm - Distance in kilometers
 * @param {number} toll - Toll charges
 * @returns {object} - Fares for all vehicle types
 */
export const calculateAllVehicleFares = (distanceKm, toll = 0) => {
  const fares = {};
  
  Object.keys(VEHICLE_RATES_2026)?.forEach(vehicleKey => {
    fares[vehicleKey] = calculateOneWayFare(
      distanceKm,
      VEHICLE_RATES_2026?.[vehicleKey],
      toll
    );
  });
  
  return fares;
};

/**
 * Calculate toll based on distance (₹2 per km standard)
 * @param {number} distanceKm - Distance in kilometers
 * @returns {number} - Toll charges
 */
export const calculateToll = (distanceKm) => {
  return Math.round(distanceKm * 2);
};

/**
 * Get vehicle fare for a specific vehicle type
 * @param {string} vehicleType - Vehicle type key
 * @param {number} distanceKm - Distance in kilometers
 * @param {number} toll - Toll charges
 * @returns {number} - Fare for the vehicle
 */
export const getVehicleFare = (vehicleType, distanceKm, toll = 0) => {
  const rate = VEHICLE_RATES_2026?.[vehicleType] || VEHICLE_RATES_2026?.marutiDzire;
  return calculateOneWayFare(distanceKm, rate, toll);
};

/**
 * Calculate fare for multi-day tour packages
 * @param {number} basePrice - Base price per day
 * @param {number} days - Number of days
 * @param {string} vehicleType - Vehicle type key
 * @returns {number} - Total tour package fare
 */
export const calculateTourPackageFare = (basePrice, days, vehicleType) => {
  const multiplier = days > 1 ? days : 1;
  return Math.round(basePrice * multiplier);
};

/**
 * Get distance between two cities (sample data - should be replaced with actual API)
 * @param {string} from - Origin city
 * @param {string} to - Destination city
 * @returns {number} - Distance in kilometers
 */
export const getDistanceBetweenCities = (from, to) => {
  const distances = {
    'delhi-ludhiana': 310,
    'ludhiana-delhi': 310,
    'chandigarh-shimla': 115,
    'chandigarh-manali': 310,
    'chandigarh-haridwar': 180,
    'delhi-chandigarh': 250,
    'chandigarh-delhi': 250,
    'chandigarh-amritsar': 230,
    'amritsar-chandigarh': 230
  };
  
  const key = `${from?.toLowerCase()}-${to?.toLowerCase()}`;
  return distances?.[key] || 200;
};

/**
 * Calculate OLA/Uber 2026 pricing comparison
 * @param {number} distanceKm - Distance in kilometers
 * @param {string} vehicleType - Vehicle type key
 * @returns {object} - Pricing comparison with OLA, Uber, and our rates
 */
export const calculateOlaUberComparison = (distanceKm, vehicleType) => {
  const ourRate = VEHICLE_RATES_2026?.[vehicleType] || VEHICLE_RATES_2026?.marutiDzire;
  const toll = calculateToll(distanceKm);
  const ourPrice = calculateOneWayFare(distanceKm, ourRate, toll);
  
  // OLA 2026 rates (slightly higher than ours)
  const olaRate = ourRate * 1.15;
  const olaPrice = Math.round((distanceKm * olaRate) + toll);
  
  // Uber 2026 rates (competitive with OLA)
  const uberRate = ourRate * 1.12;
  const uberPrice = Math.round((distanceKm * uberRate) + toll);
  
  return {
    ourPrice,
    olaPrice,
    uberPrice,
    savings: Math.round(Math.min(olaPrice, uberPrice) - ourPrice),
    toll,
    distance: distanceKm
  };
};

/**
 * Calculate accurate city-to-city fare with toll breakdown
 * @param {string} fromCity - Origin city
 * @param {string} toCity - Destination city
 * @param {string} vehicleType - Vehicle type key
 * @returns {object} - Detailed fare breakdown
 */
export const calculateCityToCity = (fromCity, toCity, vehicleType) => {
  const distance = getDistanceBetweenCities(fromCity, toCity);
  const rate = VEHICLE_RATES_2026?.[vehicleType] || VEHICLE_RATES_2026?.marutiDzire;
  const toll = calculateToll(distance);
  const baseFare = Math.round(distance * rate);
  const totalFare = baseFare + toll;
  
  const comparison = calculateOlaUberComparison(distance, vehicleType);
  
  return {
    fromCity,
    toCity,
    distance,
    vehicleType,
    vehicleName: VEHICLE_NAMES?.[vehicleType],
    ratePerKm: rate,
    baseFare,
    toll,
    totalFare,
    comparison
  };
};

export default {
  calculateOneWayFare,
  calculateAllVehicleFares,
  calculateToll,
  getVehicleFare,
  calculateTourPackageFare,
  getDistanceBetweenCities,
  calculateOlaUberComparison,
  calculateCityToCity,
  VEHICLE_RATES_2026,
  VEHICLE_NAMES
};